import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMembership } from '@/contexts/MembershipContext';
import { MembershipTier } from '@/types/membership';
import { UpgradePrompt } from '@/components/membership/UpgradePrompt';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredTier?: MembershipTier;
}

export function ProtectedRoute({ children, requiredTier }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { meetsRequirement, loading: membershipLoading, capabilities } = useMembership();

  if (authLoading || membershipLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check tier requirement if specified
  if (requiredTier && !meetsRequirement(requiredTier)) {
    // If user is Explorer (can't create collections), redirect to Explore instead of showing upgrade prompt
    if (!capabilities.canCreateCollections) {
      return <Navigate to="/explore" replace />;
    }
    return <UpgradePrompt requiredTier={requiredTier} />;
  }

  return <>{children}</>;
}
